import { ComponentPortal } from '@angular/cdk/portal';
import { ChangeDetectorRef, Component, ElementRef, Inject, ViewChild } from '@angular/core';
import SignaturePad from 'signature_pad';
import { CheckBoxThemeControl } from '../../../appbuilder/controls/checkbox/checkbox.control';
import { ComboboxThemeControl } from '../../../appbuilder/controls/combobox/combobox.theme.control';
import { TextboxThemeControl } from '../../../appbuilder/controls/textbox/textbox.theme.control';
import { GenericMenuTab } from '../../../appbuilder/menutabs/generic/generic.menu.tab';
import { BasePanel } from '../../../appbuilder/panels/base.panel';
import { PropertyGroupDisplay } from '../../../models/enums/propertygroupdisplay.enum';
import { Color } from '../../../models/style/color.model';
import { Gradient } from '../../../models/style/gradient.model';
import { PROPERTIES, PROPERTYGROUPS } from '../../../services/dynamic.component.service';
import { LayoutService } from '../../../services/layout.service';
import { IBaseComponent } from '../base.component';
import { ResizedEvent } from '../../../models/resized.event';

@Component({
    selector: 'signature-pad-control',
    templateUrl: './signature.pad.control.html',
    styleUrls: ['./signature.pad.control.css']
})
export class SignaturePadControl extends IBaseComponent {

    static Type: any = 'signaturepad';
    static Default = {
        SelectedItem: null,
        Type: 'signaturepad',
        Layout: {
            _Editable: true,
            ShowSubmit: true,
            ShowClear: true
        }
    };

    SignaturePad: SignaturePad;
    @ViewChild('canvas') canvasEl: ElementRef;

    get Erase() {
        return this.SignaturePad && this.SignaturePad.compositeOperation == 'destination-out';
    }
    set Erase(val: boolean) {
        if (this.SignaturePad) {
            this.SignaturePad.compositeOperation = val ? 'destination-out' : 'source-over';
            this.setLineWidth();
        }
    }

    constructor(cdRef: ChangeDetectorRef, @Inject(LayoutService.CONTAINER_DATA) public data) {
        super(cdRef, data);
        this.EventList.push('Submit');
        this.EventList.push('Clear');
    }

    ControlInitialized() {
        if (this.canvasEl && this.canvasEl.nativeElement) {
            this.SignaturePad = new SignaturePad(this.canvasEl.nativeElement);
            this.setOptions();
        }
    }

    onLayoutElementSet() {
        this.setOptions();
    }
    onLayoutElementChanged() {
        this.setOptions();
    }

    setOptions() {
        if (this.SignaturePad) {
            if (typeof this.LayoutElementValue.DotSize === 'number') {
                this.SignaturePad.dotSize = this.LayoutElementValue.DotSize;
            } else {
                this.SignaturePad.dotSize = 0;
            }
            let penColor = 'black';
            if (this.LayoutElementValue.Font && this.LayoutElementValue.Font.FontColor) {
                const colorText = Color.GetStyleText(this.LayoutElementValue.Font.FontColor);
                if (colorText) {
                    penColor = colorText;
                }
            }
            this.SignaturePad.penColor = penColor;
            let bgColor = 'rgba(0,0,0,0)';
            if (this.LayoutElementValue.BackgroundColor) {
                const bgc = Gradient.getStyleObject(this.LayoutElementValue.BackgroundColor)['background-color'];
                if (bgc) {
                    bgColor = bgc;
                }
            }
            if (bgColor != this.SignaturePad.backgroundColor) {
                this.SignaturePad.backgroundColor = bgColor;
                const data = this.SignaturePad.toData();
                this.SignaturePad.clear();
                if (data) {
                    this.SignaturePad.fromData(data);
                }
            }
            this.setLineWidth();
        }
    }

    setLineWidth() {
        if (this.SignaturePad) {
            let min = 0.5;
            let max = 2.5;
            if (typeof this.LayoutElementValue.MinLineWidth === 'number') {
                min = this.LayoutElementValue.MinLineWidth;
            }
            if (typeof this.LayoutElementValue.MaxLineWidth === 'number') {
                max = this.LayoutElementValue.MaxLineWidth;
            }
            if (this.Erase) {
                if (typeof this.LayoutElementValue.EraseMinLineWidth === 'number') {
                    min = this.LayoutElementValue.EraseMinLineWidth;
                }
                if (typeof this.LayoutElementValue.EraseMaxLineWidth === 'number') {
                    max = this.LayoutElementValue.EraseMaxLineWidth;
                }
            }
            if (min > max) {
                this.SignaturePad.minWidth = max;
                this.SignaturePad.maxWidth = min;
            } else {
                this.SignaturePad.minWidth = min;
                this.SignaturePad.maxWidth = max;
            }
        }
    }

    submit() {
        if (this.SignaturePad) {
            let saveType = ''
            let data;
            if (typeof this.LayoutElementValue.SaveType === 'string') {
                saveType = this.LayoutElementValue.SaveType;
            }
            switch (saveType) {
                case 'png':
                    data = this.SignaturePad.toDataURL('image/png');
                    break;
                case 'jpeg':
                    data = this.SignaturePad.toDataURL('image/jpeg');
                    break;
                case 'svg':
                    data = this.SignaturePad.toDataURL('image/svg+xml');
                    break;
                default:
                    data = this.SignaturePad.toDataURL();
                    break;
            }
            this.triggerEvent('Submit', data);
        }
    }

    clear() {
        if (this.SignaturePad) {
            this.SignaturePad.clear();
            this.triggerEvent('Clear');
        }
    }

    undo() {
        if (this.SignaturePad) {
            const data = this.SignaturePad.toData();
            if (data) {
                data.pop(); // remove the last dot or line
                this.SignaturePad.fromData(data);
            }
        }
    }

    onResize(event: ResizedEvent) {
        if (event && event.newRect && this.canvasEl && this.canvasEl.nativeElement) {
            const data = this.SignaturePad.toData();
            this.canvasEl.nativeElement.width = event.newRect.width;
            this.canvasEl.nativeElement.height = event.newRect.height;
            if (data) {
                this.SignaturePad.fromData(data);
            }
        }
    }
}

export class SignaturePanel extends BasePanel {
    static override SIDS = ['3bb95d73-e759-4087-a604-f85f331ad351'];

    static InitPanel() {
        PROPERTYGROUPS.push({
            SID: '3bb95d73-e759-4087-a604-f85f331ad351',
            ID: SignaturePadControl.Type,
            Caption: '@@SignaturePad',
            Index: 100,
            Content: GenericMenuTab,
            Display: PropertyGroupDisplay.Grid,
            Columns: ['100%'],
            Rows: ['auto'],
            CheckVisibility: (item) => {
                return item.ElementType == SignaturePadControl.Type;
            }
        });
        PROPERTIES.push({
            ID: "ShowSubmit",
            Parent: SignaturePadControl.Type,
            Content: new ComponentPortal(CheckBoxThemeControl),
            InitArgs: {
                Caption: "@@ShowSubmit"
            }
        });
        PROPERTIES.push({
            ID: "ShowUndo",
            Parent: SignaturePadControl.Type,
            Content: new ComponentPortal(CheckBoxThemeControl),
            InitArgs: {
                Caption: "@@ShowUndo"
            }
        });
        PROPERTIES.push({
            ID: "ShowClear",
            Parent: SignaturePadControl.Type,
            Content: new ComponentPortal(CheckBoxThemeControl),
            InitArgs: {
                Caption: "@@ShowClear"
            }
        });
        PROPERTIES.push({
            ID: "ShowErase",
            Parent: SignaturePadControl.Type,
            Content: new ComponentPortal(CheckBoxThemeControl),
            InitArgs: {
                Caption: "@@ShowErase"
            }
        });
        PROPERTIES.push({
            ID: "DotSize",
            Parent: SignaturePadControl.Type,
            Content: new ComponentPortal(TextboxThemeControl),
            Label: "@@DotSize",
            InitArgs: {
                InputType: "number",
                Minimum: 1
            }
        });
        PROPERTIES.push({
            ID: "MinLineWidth",
            Parent: SignaturePadControl.Type,
            Content: new ComponentPortal(TextboxThemeControl),
            Label: "@@MinLineWidth",
            InitArgs: {
                InputType: "number",
                Minimum: 1
            }
        });
        PROPERTIES.push({
            ID: "MaxLineWidth",
            Parent: SignaturePadControl.Type,
            Content: new ComponentPortal(TextboxThemeControl),
            Label: "@@MaxLineWidth",
            InitArgs: {
                InputType: "number",
                Minimum: 1
            }
        });
        PROPERTIES.push({
            ID: "EraseMinLineWidth",
            Parent: SignaturePadControl.Type,
            Content: new ComponentPortal(TextboxThemeControl),
            Label: "@@EraseMinLineWidth",
            InitArgs: {
                InputType: "number",
                Minimum: 1
            },
            CheckVisibility: (item) => {
                return item.ShowErase;
            },
        });
        PROPERTIES.push({
            ID: "EraseMaxLineWidth",
            Parent: SignaturePadControl.Type,
            Content: new ComponentPortal(TextboxThemeControl),
            Label: "@@EraseMaxLineWidth",
            InitArgs: {
                InputType: "number",
                Minimum: 1
            },
            CheckVisibility: (item) => {
                return item.ShowErase;
            },
        });
        PROPERTIES.push({
            ID: "SaveType",
            Parent: SignaturePadControl.Type,
            Content: new ComponentPortal(ComboboxThemeControl),
            Label: "@@SaveType",
            InitArgs: {
                Placeholder: "@@SaveType",
                Multiple: false,
                ItemsSource: [{
                    Caption: '@@PNG',
                    Value: 'png'
                }, {
                    Caption: '@@JPEG',
                    Value: 'jpeg'
                }, {
                    Caption: '@@SVG',
                    Value: 'svg'
                }],
                ValueMemberPath: "Value",
                DisplayMemberPath: "Caption"
            }
        });
    }
}
